import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { logEvent } from "@/hooks/use-event-log";
import type { ChecklistItem } from "@/lib/db-types";
import { DEFAULT_CLOSING_CHECKLIST } from "@/components/project/ClosingChecklistDialog";
import { isProjectLockedError, toastProjectLocked } from "@/lib/db-errors";

export function useProjectMutations(projectId: string | undefined) {
  const qc = useQueryClient();
  const { activeUser } = useImpersonation();
  const navigate = useNavigate();

  const optimisticUpdate = (patch: Record<string, unknown>) => {
    qc.setQueryData(["project-detail", projectId], (old: { project: Record<string, unknown> } | undefined) => {
      if (!old) return old;
      return { ...old, project: { ...old.project, ...patch } };
    });
  };

  const invalidate = () => invalidateFor(qc, "projects", { projectId });

  const updateProject = async (data: Record<string, unknown>) => {
    const { error } = await supabase.from("projects").update(data).eq("id", projectId);
    if (error) throw error;
    invalidate();
  };

  const handleProjectPhaseChange = async (newPhase: string) => {
    try {
      optimisticUpdate({ project_phase: newPhase });
      await updateProject({ project_phase: newPhase });
      toast.success("Fase endret");
      logEvent({
        event_type: "project.phase_changed",
        entity_type: "project",
        entity_id: projectId,
        project_id: projectId,
        user_id: activeUser?.id,
        payload: { to: newPhase },
      });
    } catch (e: unknown) {
      if (isProjectLockedError(e)) {
        toastProjectLocked({ projectId, navigate });
        invalidate();
        return;
      }
      console.error("Feil ved oppdatering av prosjektfase:", e);
      toast.error("Noe gikk galt");
      invalidate();
    }
  };

  const updateProjectStatus = async (newStatus: string) => {
    try {
      optimisticUpdate({ project_status: newStatus });
      await updateProject({ project_status: newStatus });
      toast.success("Prosjektstatus oppdatert");
      logEvent({
        event_type: "project.status_changed",
        entity_type: "project",
        entity_id: projectId,
        project_id: projectId,
        user_id: activeUser?.id,
        payload: { to: newStatus },
      });
    } catch (e: unknown) {
      if (isProjectLockedError(e)) {
        toastProjectLocked({ projectId, navigate });
        invalidate();
        return;
      }
      console.error("Feil ved oppdatering av projektstatus:", e);
      toast.error("Noe gikk galt");
      invalidate();
    }
  };

  const confirmClose = async (closingChecklist: ChecklistItem[]) => {
    try {
      const completedAt = new Date().toISOString();
      optimisticUpdate({
        project_status: "completed",
        closing_checklist: closingChecklist,
        completed_at: completedAt,
      });
      await updateProject({
        project_status: "completed",
        closing_checklist: closingChecklist,
        completed_at: completedAt,
      });
      toast.success("Prosjekt ferdigstilt");
      logEvent({
        event_type: "project.completed",
        entity_type: "project",
        entity_id: projectId,
        project_id: projectId,
        user_id: activeUser?.id,
      });
    } catch (e: unknown) {
      if (isProjectLockedError(e)) {
        toastProjectLocked({ projectId, navigate });
        invalidate();
        return;
      }
      console.error("Feil ved lukking av prosjekt:", e);
      toast.error("Noe gikk galt");
      invalidate();
    }
  };

  const confirmActivate = async () => {
    try {
      optimisticUpdate({ project_status: "active" });
      await updateProject({ project_status: "active" });
      toast.success("Prosjekt aktivert");
      logEvent({
        event_type: "project.activated",
        entity_type: "project",
        entity_id: projectId,
        project_id: projectId,
        user_id: activeUser?.id,
      });
    } catch (e: unknown) {
      if (isProjectLockedError(e)) {
        toastProjectLocked({ projectId, navigate });
        invalidate();
        return;
      }
      console.error("Feil ved aktivering av prosjekt:", e);
      toast.error("Noe gikk galt");
      invalidate();
    }
  };

  const reopen = async () => {
    try {
      optimisticUpdate({ project_status: "active", completed_at: null });
      await updateProject({ project_status: "active", completed_at: null });
      toast.success("Prosjekt åpnet på nytt");
      logEvent({
        event_type: "project.reopened",
        entity_type: "project",
        entity_id: projectId,
        project_id: projectId,
        user_id: activeUser?.id,
      });
    } catch (e: unknown) {
      if (isProjectLockedError(e)) {
        toastProjectLocked({ projectId, navigate });
        invalidate();
        return;
      }
      console.error("Feil ved gjenåpning av prosjekt:", e);
      toast.error("Kunne ikke åpne prosjektet på nytt");
      invalidate();
    }
  };

  return {
    handleProjectPhaseChange,
    updateProjectStatus,
    confirmClose,
    confirmActivate,
    reopen,
    DEFAULT_CLOSING_CHECKLIST,
  };
}
